import { ActionIcon, TextInput } from '@mantine/core'

import IconTrash from 'src/icons/IconTrash'

import {
  generateValueFromLabel,
  isValidLabelForValue,
} from './customFieldsUtils'

interface OptionItem {
  id: string
  label: string
  value: string
  sortOrder: number
}

interface CustomFieldOptionsManagerProps {
  options: OptionItem[]
  onChange: (options: OptionItem[]) => void
  error?: string
}

const CustomFieldOptionsManager = ({
  options,
  onChange,
  error,
}: CustomFieldOptionsManagerProps) => {
  const handleAddOption = () => {
    const newOption: OptionItem = {
      id: `temp-${Date.now()}`,
      label: '',
      value: '',
      sortOrder: options.length,
    }
    onChange([...options, newOption])
  }

  const handleRemoveOption = (id: string) => {
    const filtered = options.filter((opt) => opt.id !== id)
    // Reindex sortOrder
    const reindexed = filtered.map((opt, idx) => ({
      ...opt,
      sortOrder: idx,
    }))
    onChange(reindexed)
  }

  const handleUpdateOption = (id: string, label: string) => {
    const updated = options.map((opt) =>
      opt.id === id
        ? {
            ...opt,
            label,
            value: generateValueFromLabel(label),
          }
        : opt
    )
    onChange(updated)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-doubleNickel-gray-700">
          Options <span className="text-doubleNickel-error-500">*</span>
        </label>
        <button
          type="button"
          onClick={handleAddOption}
          className="text-sm text-doubleNickel-primary-600 hover:text-doubleNickel-primary-700"
        >
          + Add Option
        </button>
      </div>

      {options.length === 0 && (
        <div className="rounded-md border border-doubleNickel-gray-300 bg-doubleNickel-gray-50 p-4 text-center text-sm text-doubleNickel-gray-500">
          No options added yet. Click "Add Option" to create one.
        </div>
      )}

      <div className="flex flex-col gap-2">
        {options.map((option, index) => (
          <div key={option.id} className="flex items-start gap-2">
            <div className="flex-1">
              <TextInput
                placeholder={`Option ${index + 1}`}
                value={option.label}
                onChange={(e) => handleUpdateOption(option.id, e.target.value)}
                error={
                  !option.label.trim()
                    ? 'Option label is required'
                    : !isValidLabelForValue(option.label)
                      ? 'Label must contain at least one letter or number'
                      : undefined
                }
              />
              {option.value && (
                <div className="mt-1 text-xs text-doubleNickel-gray-500">
                  Value: {option.value}
                </div>
              )}
            </div>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => handleRemoveOption(option.id)}
              disabled={options.length === 1}
              title={
                options.length === 1
                  ? 'At least one option is required'
                  : 'Remove option'
              }
            >
              <IconTrash className="h-4 w-4" />
            </ActionIcon>
          </div>
        ))}
      </div>

      {error && (
        <div className="text-sm text-doubleNickel-error-500">{error}</div>
      )}
    </div>
  )
}

export default CustomFieldOptionsManager
